import type { CookieParseOptions, CookieSerializeOptions } from "cookie-es";
import { parse, serialize } from "cookie-es";
import { destr } from "destr";
import type { Ref } from "vue";
import { ref, toValue, watch } from "vue";

export type CookiePrimitive = string | number | boolean | null;
export type CookieValue =
  | CookiePrimitive
  | CookiePrimitive[]
  | Record<string, CookiePrimitive>;

type _CookieOptions = Omit<
  CookieSerializeOptions & CookieParseOptions,
  "decode" | "encode"
>;

export interface CookieOptions<
  T extends CookieValue = string,
> extends _CookieOptions {
  decode?(value: string): T;
  encode?(value: T): string;
  default?: () => T | Ref<T>;
  watch?: boolean | "shallow";
}

export type CookieRef<T extends CookieValue> = Ref<T>;

const CookieDefaults: CookieOptions<CookieValue> = {
  path: "/",
  watch: true,
  decode: (val) => destr<CookieValue>(decodeURIComponent(val)),
  encode: (val) =>
    encodeURIComponent(typeof val === "string" ? val : JSON.stringify(val)),
};

export const useCookie = <T extends CookieValue = string>(
  name: string,
  _opts?: CookieOptions<T>,
): CookieRef<T> => {
  const opts = { ...CookieDefaults, ...(_opts || {}) } as CookieOptions<T>;
  const cookies = parse(document.cookie, opts as CookieParseOptions) as Record<
    string,
    T
  >;

  const cookie = ref<T>(
    (cookies[name] ?? toValue(opts.default?.())) as T,
  ) as Ref<T>;

  watch(cookie, () => {
    document.cookie = serializeCookie(
      name,
      cookie.value,
      opts as CookieSerializeOptions,
    );
  });

  return cookie;
};

function serializeCookie(
  name: string,
  value: CookieValue,
  opts: CookieSerializeOptions = {},
) {
  const serializedValue =
    typeof value === "string" ? value : JSON.stringify(value);

  if (value === null) return serialize(name, "", { ...opts, maxAge: -1 });

  return serialize(name, serializedValue, {
    ...opts,
    maxAge: 60 * 60 * 24 * 30,
  });
}
